import React, { useEffect, useState } from 'react';
import { X, Check, Star, Users, Plane, ChevronLeft, ChevronRight } from 'lucide-react';

const PlatinumMembershipModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    // Show modal after a short delay
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
  };

  const handleStartTrial = () => {
    // Close the modal first
    handleClose();
    
    // Scroll to the packages section
    const packagesSection = document.getElementById('packages');
    if (packagesSection) {
      const headerHeight = document.querySelector('header')?.offsetHeight || 0;
      const scrollPadding = 10;
      const targetPosition = packagesSection.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: targetPosition - headerHeight - scrollPadding,
        behavior: 'smooth'
      });
    }
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % 2);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + 2) % 2);
  };

  const offers = [
    {
      id: 'group-training',
      icon: <Users size={24} />,
      title: 'Small Group Training',
      subtitle: 'Group Sessions',
      price: '$135',
      description: 'Join our small group training sessions for a more affordable yet effective workout experience. Perfect for those who enjoy group motivation.',
      features: [
        'Small group setting',
        'Expert guidance',
        'Motivating atmosphere',
        'Regular schedule',
        'New client discount'
      ],
      buttonText: 'Start Today',
      color: 'purple'
    },
    {
      id: 'travel-kit',
      icon: <Plane size={24} />,
      title: 'PHYT Travel Kit',
      subtitle: '5 min sessions',
      price: '$9.99',
      description: 'Stay mobile and flexible with our 5-minute mobility program. Perfect for busy professionals and travelers.',
      features: [
        'Quick mobility routines',
        'Travel-friendly exercises',
        'Digital access',
        'No equipment needed',
        'Instant access'
      ],
      buttonText: 'Start Today',
      color: 'purple'
    }
  ];

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl p-4 sm:p-6 md:p-8 max-w-2xl w-full mx-auto transform transition-all duration-300 scale-100 animate-fade-in-up shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X size={20} className="sm:w-6 sm:h-6" />
        </button>

        {/* Carousel Container */}
        <div className="relative overflow-hidden">
          <div 
            className="flex transition-transform duration-300 ease-in-out"
            style={{ transform: `translateX(-${currentSlide * 100}%)` }}
          >
            {offers.map((offer, index) => (
              <div key={offer.id} className="w-full flex-shrink-0">
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-800 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium mb-3 sm:mb-4">
                    {offer.icon}
                    {offer.subtitle}
                  </div>
                  
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                    {offer.title}
                  </h2>
                  
                  <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 max-w-lg mx-auto">
                    {offer.description}
                  </p>
                  
                  <div className="mb-6 sm:mb-8">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <span className="text-4xl sm:text-5xl font-bold text-purple-600">{offer.price}</span>
                    </div>
                  </div>
                    
                  <div className="space-y-3 text-left bg-purple-50 p-4 sm:p-6 rounded-xl mb-6 sm:mb-8">
                    <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-center text-sm sm:text-base">What's Included:</h3>
                    
                    <div className="grid grid-cols-1 gap-3">
                      {offer.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-start gap-2 sm:gap-3">
                          <Check size={16} className="sm:w-[18px] sm:h-[18px] text-purple-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 font-medium text-sm sm:text-base">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <button
                    onClick={handleStartTrial}
                    className="w-full bg-purple-600 text-white px-6 py-3 sm:px-8 sm:py-4 rounded-xl text-lg sm:text-xl font-semibold hover:bg-purple-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
                  >
                    {offer.buttonText}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-600 hover:text-purple-600 p-2 rounded-full shadow-lg transition-all duration-300"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white text-gray-600 hover:text-purple-600 p-2 rounded-full shadow-lg transition-all duration-300"
          >
            <ChevronRight size={20} />
          </button>

          {/* Dots Indicator */}
          <div className="flex justify-center mt-4 space-x-2">
            {offers.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  currentSlide === index ? 'bg-purple-600 w-6' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Custom animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.5s ease-out;
        }
      `}</style>
    </div>
  );
};

export default PlatinumMembershipModal; 